import React, { ReactElement, Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Fade, Spinner, styled } from '@volkswagen-onehub/components-core';
import {
  DataSourceTypes,
  EngineTypes,
  GiveawayResponse,
  ItemNotFoundError,
  TaskStatus,
} from '@org-avp/avp-hck-dga-customerui-commons';
import { useSearchParams } from 'react-router-dom';
import { BrandIdentifier } from '@volkswagen-onehub/gfa-dealer-context-service';
import { Brand, Market } from '@org-avp/models';
import { BeautyShot } from '@/components/beautyshot/beautyshot';

import { CameraFlight } from '@/components/cameraflight/cameraflight';
import { Consumption } from '@/components/consumption/consumption';
import { YourCar } from '@/components/yourcar/yourcar';
import { Exterior } from '@/components/exterior/exterior';
import { Interior } from '@/components/interior/interior';
import { LoadingSpinner } from '@/components/loadingspinner/loadingspinner';
import { GiveawayContext, initialState } from '@/state/state';
import { useInterval } from '@/hooks/useInterval';
import { getI18nLocale } from '@/util/localization';
import { maximalScreenWidth, minimalScreenWidth, size } from '@/common/breakpoints';
import { ErrorSite } from '@/components/errorSite/errorSite';
import { isMissingAssets, isMissingCameraFlight, isRetryable } from '@/util/dga-api';
import { useApiClient } from '@/contexts/ApiClientContext';
import { KeyFacts } from '@/components/keyfacts/keyfacts';
import { useFeatureServices } from '@/contexts/FeatureServicesContext';
import { FeatureService } from '@/enums/FeatureServiceDefinition';
import { extractMyDealerId } from '@/util/dealer-data';
import { extractBrand } from '@/util/brand';
import { TrackingEvent } from '@/util/tracking/definitions';
import { useTrackingService } from '@/contexts/TrackingServiceContext';
import { ExpiredSite } from '@/components/expiredSite/expiredSite';
import { DetailGallery } from '@/components/detail/detailGallery';
import { requiresEnergyEfficiencyLabel } from '@/components/energyEfficiencyLabel/helper';
import { DealerSearchDetailFA } from '@/components/dealerSearchDetail/DealerSearchDetailFA';
import { DEALER_ID_TESTING } from '@/util/dealerWhitelist';
import { CallToActionBox } from '@/components/callToActionBox/callToActionBox';
import { Welcome } from '@/components/welcome/welcome';
import { LegalNotice } from '@/components/legalNotice/legalNotice';
import { VehicleInformation } from '@/components/vehicleInformation/vehicleInformation';
import { DialogModal } from '@/components/dialog/Dialog';
import useBreakpoint from '@/hooks/useBreakpoint';
import { LazyInView } from '@/components/lazyInView/LazyInView';

const CertifiedPreOwnedLoader = React.lazy(
  () => import('@/components/certifiedPreOwned/certifiedPreOwned'),
);

const LazyEnergyEfficiencyLabelLoader = React.lazy(
  () => import('@/components/energyEfficiencyLabel/EnergyEfficiencyLabelLoader'),
);

const LazyTurntableLoader = React.lazy(() => import('@/components/turntable/turntable'));

const LazyElliCtaLoader = React.lazy(() => import('@/components/elliCtaBanner/elliCtaBanner'));

const LazyWebstreamingOverlay = React.lazy(
  () => import('@/components/streaming/WebStreamingOverlay'),
);

const StyledView = styled.div`
  width: 100%;

  @media screen ${minimalScreenWidth.l}) {
    height: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const StyledApp = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  transition: width 0.1s;
  margin: 0 auto;
  @media ${minimalScreenWidth.xxxl} {
    --max-width: ${size.xxxl};
  }
  @media ${minimalScreenWidth.xxl} and ${maximalScreenWidth.xxxl} {
    --max-width: ${size.xxxl};
  }
  @media ${minimalScreenWidth.xl} and ${maximalScreenWidth.xxl} {
    --max-width: ${size.xxl};
  }
  @media ${minimalScreenWidth.l} and ${maximalScreenWidth.xl} {
    --max-width: ${size.xl};
  }
  @media ${minimalScreenWidth.m} and ${maximalScreenWidth.l} {
    --max-width: ${size.l};
  }
  @media ${minimalScreenWidth.s} and ${maximalScreenWidth.m} {
    --max-width: ${size.m};
  }
  max-width: var(--max-width);
`;

const StyledMobileContent = styled.div`
  flex: flex;
  flex-direction: column;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const POLLING_INTERVAL = 10000;
const ANIMATION_DURATION = 1000;

const getGalleryComponents = (withElliCta: boolean): ReactElement[] => {
  return [
    <LazyInView key="exterior">
      <Exterior />
    </LazyInView>,
    <LazyInView key="interior">
      <Interior />
    </LazyInView>,
    ...(withElliCta
      ? [
          <LazyInView rootMargin="300px" key="elliBanner">
            <Suspense fallback={<Spinner variant="default" />} key="pOwned">
              <LazyElliCtaLoader />
            </Suspense>
          </LazyInView>,
        ]
      : []),
    <LazyInView key="detail">
      <DetailGallery />
    </LazyInView>,
  ];
};

const getComponentsSequence = (
  withCamFlight: boolean,
  withConsumptionLayer: boolean,
  withEquipmentOptions: boolean,
  withCertifiedPreOwnedAndAssets: boolean,
  withElliCta: boolean,
  withCertifiedPreOwned: boolean,
  withTechnicalDataFeature: boolean,
  withShow3DStream: boolean,
): ReactElement[] => {
  if (withCamFlight) {
    return [
      ...(withShow3DStream
        ? [<LazyWebstreamingOverlay key="webStreamingOverlay" />]
        : [<BeautyShot key="beautyShot" />]),
      ...(withCertifiedPreOwned ? [<LegalNotice />] : []),
      <YourCar requiresSmallerBottomSpacing={withConsumptionLayer} key="yourcar" />,
      ...(withConsumptionLayer ? [<Consumption key="consumption" />] : []),
      ...(withCertifiedPreOwnedAndAssets
        ? [
            <LazyInView key="pOwned" rootMargin="400px">
              <Suspense fallback={<Spinner variant="default" />}>
                <CertifiedPreOwnedLoader />
              </Suspense>
            </LazyInView>,
          ]
        : []),
      <KeyFacts key="keyfacts" />,
      ...(withShow3DStream ? [] : [<CameraFlight key="cameraFlight" />]),
      <VehicleInformation
        showEquipmentFeature={withEquipmentOptions}
        showTechnicalDataFeature={withTechnicalDataFeature}
      />,
      <LazyInView key="turntable">
        <Suspense fallback={<Spinner variant="default" />}>
          <LazyTurntableLoader />
        </Suspense>
      </LazyInView>,
      ...getGalleryComponents(withElliCta),
    ];
  }

  return [
    ...(withShow3DStream
      ? [<LazyWebstreamingOverlay key="webStreamingOverlay" />]
      : [<BeautyShot key="beautyShot" />]),
    ...(withCertifiedPreOwned ? [<LegalNotice />] : []),
    <YourCar requiresSmallerBottomSpacing={withConsumptionLayer} key="yourcar" />,
    ...(withConsumptionLayer ? [<Consumption key="consumption" />] : []),
    ...(withCertifiedPreOwnedAndAssets
      ? [
          <Suspense fallback={<Spinner variant="default" />} key="pOwned">
            <CertifiedPreOwnedLoader />
          </Suspense>,
        ]
      : []),
    <KeyFacts key="keyfacts" />,
    ...(withShow3DStream
      ? []
      : [
          <LazyInView key="turntable">
            <Suspense fallback={<Spinner variant="default" />}>
              <LazyTurntableLoader />
            </Suspense>
          </LazyInView>,
        ]),
    <VehicleInformation
      showEquipmentFeature={withEquipmentOptions}
      showTechnicalDataFeature={withTechnicalDataFeature}
    />,
    ...getGalleryComponents(withElliCta),
  ];
};

export const Giveaway = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const giveawayId = searchParams.get('id');
  const { i18n, t } = useTranslation();
  const [giveawayData, setGiveawayData] = useState<GiveawayResponse>({ ...initialState });
  const [isLoading, setIsLoading] = useState(true);
  const [responseStatus, setResponseStatus] = useState<number>();
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageSrc: '',
  });

  const apiService = useApiClient();
  const myDealerService = useFeatureServices()[FeatureService.MYDEALER_SERVICE];
  const trackingService = useTrackingService();
  const pageInfoService = useFeatureServices()[FeatureService.PAGE_INFO_SERVICE];

  const { isMobile, isTablet, isSmallDesktop } = useBreakpoint();
  const isSmallScreen = isMobile || isTablet || isSmallDesktop;

  const fetchGiveaway = useCallback(async () => {
    try {
      const response = await apiService.getOrder(giveawayId as string);

      setGiveawayData(response);
      setResponseStatus(200);

      if (response.assets && response.assets.length !== 0) {
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      if (e instanceof ItemNotFoundError) {
        setResponseStatus(e.statusCode);
      }
      if (e instanceof TypeError) {
        // is thrown in case of errors like missing cors headers in response
        setResponseStatus(400);
      }
      console.error(e);
    }
  }, [apiService, giveawayId]);

  useEffect(() => {
    if (!myDealerService) {
      return undefined;
    }

    if (isLoading) {
      myDealerService.setSelectedDealer(undefined);
      return undefined;
    }

    const id = extractMyDealerId(giveawayData.dealerId);
    const brand = (giveawayData.brand ??
      extractBrand(giveawayData.marketingCode)) as BrandIdentifier;

    if (id && brand) {
      myDealerService.setSelectedDealer({ id, brand, market: giveawayData.market });
    }

    return () => {
      if (myDealerService) {
        myDealerService.setSelectedDealer(undefined);
      }
    };
  }, [giveawayData, isLoading, myDealerService]);

  useEffect(() => {
    trackingService.updateEventData(giveawayData);
  }, [giveawayData, trackingService]);

  useEffect(() => {
    void i18n.changeLanguage(getI18nLocale(giveawayData.locale));
  }, [i18n, giveawayData]);

  useEffect(() => {
    if (!giveawayId) {
      console.error('DGA id is missing.');
      setResponseStatus(404);
      return;
    }
    void fetchGiveaway();
  }, [fetchGiveaway, giveawayId]);

  useEffect(() => {
    const brand = giveawayData.brand ?? extractBrand(giveawayData.marketingCode);

    const pageInfo = {
      seo: {
        pageTitle: 'Digital Giveaway',
        description: 'Discover your tailored digital giveaway built around your car configuration',
        og: {
          ogTitle: 'Digital Giveaway',
          ogDescription:
            'Discover your tailored digital giveaway built around your car configuration',
          ogType: 'website',
          ogImage: giveawayData.assets.find((asset) => asset.metadata.section === 'beauty-shot')
            ?.url,
        },
      },
    };

    switch (brand) {
      case Brand.VW_NUTZFAHRZEUGE: {
        pageInfoService.setPageInfo({
          ...pageInfo,
          logoCategory: 'commercial',
        });
        break;
      }
      case Brand.VW: {
        pageInfoService.setPageInfo({
          ...pageInfo,
          logoCategory: 'private',
        });
        break;
      }
      default: {
        pageInfoService.setPageInfo({
          ...pageInfo,
          logoCategory: undefined,
        });
        break;
      }
    }
  }, [giveawayData, pageInfoService]);

  useInterval(
    fetchGiveaway,
    POLLING_INTERVAL,
    isRetryable(responseStatus) &&
      (isMissingAssets(giveawayData) || isMissingCameraFlight(giveawayData)),
  );

  useEffect(() => {
    if (!isLoading && responseStatus === 200) {
      // (re-)setting the mydealer context requires a certain delay, otherwise the required dealer data
      // will be missing in the event data written to the tracking data layer
      setTimeout(
        () =>
          trackingService.track(TrackingEvent.VWBasicGenericFeatureAppLoad, {
            eventInfo: { contentId: `loaded feature app` },
          }),
        10000,
      );
    }
  }, [isLoading, responseStatus, trackingService]);

  const pilotMarkets = [Market.ITA, Market.CHE];
  const shouldShow3DStream = pilotMarkets.includes(giveawayData?.market);

  if (responseStatus && responseStatus >= 400 && responseStatus < 500) {
    return <ErrorSite />;
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (giveawayData.giveawayStatus === TaskStatus.EXPIRED) {
    return <ExpiredSite />;
  }

  const components = getComponentsSequence(
    !!giveawayData.renderCameraFlight,
    !!giveawayData.showWltpLayer,
    // Fallback for DGAs which are too old and not have the equipment feature. Can be removed in the future.
    !!giveawayData.equipmentOptions?.length,
    // Show Certified Pre Owned DGA component only for certified VW used Cars with available dealerAssets from VTP
    !!giveawayData.isCertifiedPreOwned && !!giveawayData.dealerAssets?.length,
    giveawayData.market === Market.DEU &&
      (giveawayData?.engineType === EngineTypes.ELECTRIC ||
        giveawayData?.engineType === EngineTypes.PLUGINHYBRID),
    !!giveawayData.isCertifiedPreOwned,
    !!giveawayData.technicalData?.length,
    shouldShow3DStream,
  );

  return (
    <StyledView>
      <StyledApp data-testid="giveaway-container">
        <GiveawayContext.Provider value={{ ...giveawayData, imageModal, setImageModal }}>
          <DialogModal
            isModalOpen={imageModal.isOpen}
            handleOpenModal={() =>
              setImageModal({ isOpen: !imageModal.isOpen, imageSrc: imageModal.imageSrc })
            }
          >
            <StyledImage src={imageModal.imageSrc} alt={t('imageModal.altText')} />
          </DialogModal>
          {(isSmallScreen || !shouldShow3DStream) && <Welcome />}
          <div role="main">
            <Fade duration={ANIMATION_DURATION}>
              {components[0]}
              {components[1]}
              {components[2]}
              {components[3]}
            </Fade>
            <StyledMobileContent>
              {components.slice(4).map((component) => (
                <Fade duration={ANIMATION_DURATION} key={component.key}>
                  {component}
                </Fade>
              ))}
            </StyledMobileContent>
            {giveawayData?.dealerId &&
              !DEALER_ID_TESTING.has(giveawayData?.dealerId) &&
              giveawayData?.dataSource !== DataSourceTypes.CARLOS && <DealerSearchDetailFA />}

            {requiresEnergyEfficiencyLabel(giveawayData) && (
              <LazyInView key="eecLabelSuspense">
                <Suspense fallback={<Spinner variant="default" />}>
                  <LazyEnergyEfficiencyLabelLoader key="eecLabel" />
                </Suspense>
              </LazyInView>
            )}

            <CallToActionBox />
          </div>
        </GiveawayContext.Provider>
      </StyledApp>
    </StyledView>
  );
};
